
import { useState } from 'react';
import { ChevronRight, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import Logo from '@/icons/Logo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Category } from '@/types/assessment';
import { getCategoryProgressPercentage, isQuestionFullyAnswered } from '@/utils/assessmentHelpers';

interface AssessmentSidebarProps {
  categories: Category[];
  currentCategoryIndex: number;
  currentQuestionIndex?: number;
  responses?: Record<string, any>;
  onNavigateToQuestion?: (categoryIndex: number, questionIndex: number) => void;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  assessmentTitle?: string;
}

export const AssessmentSidebar = ({ 
  categories, 
  currentCategoryIndex,
  currentQuestionIndex = 0,
  responses = {},
  onNavigateToQuestion,
  sidebarOpen,
  toggleSidebar,
  assessmentTitle = "Assessment"
}: AssessmentSidebarProps) => {
  const [expandedCategories, setExpandedCategories] = useState<Record<number, boolean>>(() => {
    // Initialize with current category expanded
    const initialState: Record<number, boolean> = {};
    initialState[currentCategoryIndex] = true;
    return initialState;
  });

  const toggleCategoryExpansion = (categoryIndex: number) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };
  
  // Calculate progress for each category based on completed questions
  const getCategoryProgress = (categoryIndex: number) => {
    // Always calculate actual completion based on answered questions
    return getCategoryProgressPercentage(categories[categoryIndex].questions, responses);
  };

  // Check if a question has been fully answered (both current and target selections)
  const isQuestionAnswered = (questionId: string) => {
    return isQuestionFullyAnswered(responses[questionId]);
  };

  return (
    <aside 
      className={cn(
        "bg-[#202020] text-white w-96 shrink-0 md:relative fixed h-full md:h-auto z-10 transition-all duration-300",
        sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      )}
    >
      <div className="p-6 flex items-center gap-2">
        <Logo className="h-8 w-8" />
        <div>
          <h2 className="text-lg font-bold mb-0 text-white">{assessmentTitle}</h2>
          <p className="text-xs text-white/70">Assessment Navigation</p>
        </div>
        
        {/* Close button for mobile */}
        <button 
          className="absolute top-4 right-4 text-white md:hidden"
          onClick={toggleSidebar}
          aria-label="Close navigation"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <nav className="px-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        <ul className="space-y-1">
          {categories.map((category, categoryIndex) => (
            <li key={category.id} className="mb-4">
              <Collapsible 
                open={expandedCategories[categoryIndex]} 
                onOpenChange={() => toggleCategoryExpansion(categoryIndex)}
              >
                <CollapsibleTrigger asChild>
                  <div 
                    className={cn(
                      "px-4 py-3 rounded flex items-center justify-between cursor-pointer",
                      categoryIndex === currentCategoryIndex ? 
                        "bg-[#e87a2e]/20 border-l-4 border-[#e87a2e] text-white" : 
                        "text-white/70 hover:bg-white/5"
                    )}
                    onClick={() => onNavigateToQuestion && onNavigateToQuestion(categoryIndex, 0)}
                  >
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "flex items-center justify-center rounded-full w-6 h-6 text-white text-xs font-medium",
                        categoryIndex === currentCategoryIndex ? "bg-[#e87a2e]" : "bg-gray-600"
                      )}>
                        {categoryIndex + 1}
                      </div>
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    
                    {expandedCategories[categoryIndex] ? 
                      <ChevronDown className="h-4 w-4 text-white/70" /> : 
                      <ChevronRight className="h-4 w-4 text-white/70" />
                    }
                  </div>
                </CollapsibleTrigger>
                
                {/* Progress bar for category */}
                <div className="mt-2 px-4">
                  <div className="h-1 bg-gray-700 rounded overflow-hidden">
                    <div 
                      className="h-full bg-[#e87a2e]" 
                      style={{ width: `${getCategoryProgress(categoryIndex)}%` }}
                    />
                  </div>
                  <div className="text-xs text-white/50 mt-1">
                    {getCategoryProgress(categoryIndex)}% Complete
                  </div>
                </div>

                {/* Question indicators */}
                <CollapsibleContent>
                  <div className="mt-3 pl-8 pr-4 grid grid-cols-8 gap-1">
                    {category.questions.map((question, questionIndex) => (
                      <button
                        key={question.id}
                        className={cn(
                          "w-4 h-4 rounded-full text-xs flex items-center justify-center transition-colors",
                          categoryIndex === currentCategoryIndex && questionIndex === currentQuestionIndex
                            ? "bg-[#e87a2e] text-white" // Current question
                            : isQuestionAnswered(question.id)
                              ? "bg-green-500 text-white" // Fully answered question
                              : "bg-gray-600 hover:bg-gray-500 text-white" // Unanswered question
                        )}
                        onClick={() => onNavigateToQuestion && onNavigateToQuestion(categoryIndex, questionIndex)}
                        title={`Question ${questionIndex + 1}`}
                      >
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
};
